import React, { useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { sendBuyflowExit, isInsideWebView } from "../utils/nativeNavigation";
import { useAuthCredentials } from "../hooks/useAuthCredentials";
import { useOrderMedication } from "../hooks/useOrderMedication";
import { confirmPayment } from "../modules/refill/confirmPayment";
import { confirmDelivery } from "../modules/refill/confirmDelivery";
import { getManufacturerWebsiteLink } from "../modules/refill/manufacturerLink";
import { FulfillmentPageShell } from "../components/prescriptions/FulfillmentPageShell";

type FulfillmentState = "pending_payment" | "pending_delivery";

const TITLE = "How your prescription is filled";
const PAYMENT_SUCCESS_TOAST = "Thanks — we've recorded your payment";
const DELIVERY_SUCCESS_TOAST = "Your medication delivery has been confirmed";
const ERROR_TOAST = "Something went wrong, try again";

export const PrescriptionFulfillmentDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rawState = searchParams.get("state");
  const state: FulfillmentState =
    rawState === "pending_delivery" ? "pending_delivery" : "pending_payment";
  const orderId = searchParams.get("orderId");

  const isPendingPayment = state === "pending_payment";

  const { accessCode, accessToken } = useAuthCredentials();
  const { medicationName, medicationImageUrl, internalDrugId } = useOrderMedication(orderId);
  const manufacturerLink = getManufacturerWebsiteLink(internalDrugId);

  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  const apiCall = isPendingPayment ? confirmPayment : confirmDelivery;
  const successToast = isPendingPayment ? PAYMENT_SUCCESS_TOAST : DELIVERY_SUCCESS_TOAST;

  const primaryDisabled = submitting || !orderId || !accessCode || !accessToken;

  const handlePrimary = async () => {
    if (submittingRef.current) return;
    if (!orderId || !accessCode || !accessToken) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      await apiCall(accessCode, accessToken, orderId);
      if (isInsideWebView()) {
        sendBuyflowExit({ toast: successToast });
      } else {
        window.alert(successToast);
        submittingRef.current = false;
        setSubmitting(false);
      }
    } catch {
      if (isInsideWebView()) {
        sendBuyflowExit({ toast: ERROR_TOAST });
      } else {
        window.alert(ERROR_TOAST);
      }
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const handleDone = () => {
    if (isInsideWebView()) {
      sendBuyflowExit();
    } else {
      navigate(-1);
    }
  };

  const steps = [
    {
      heading: "Complete payment",
      body: "The manufacturer's pharmacy will reach out by text to confirm your order, payment, and delivery details.",
      completed: !isPendingPayment,
    },
    {
      heading: "Wait for your medication to arrive",
      body: "Your medication will ship once you complete payment. You'll receive updates directly from the pharmacy.",
    },
  ];

  return (
    <FulfillmentPageShell
      title={TITLE}
      medicationName={medicationName}
      medicationImageUrl={medicationImageUrl}
      steps={steps}
      helpLink={manufacturerLink}
      helpLinkFallbackText="the manufacturer's website"
      primaryLabel={isPendingPayment ? "I've paid" : "I received it"}
      onPrimary={handlePrimary}
      primaryDisabled={primaryDisabled}
      submitting={submitting}
      onDone={handleDone}
    />
  );
};
